import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import fondoLogin from '../Imatges/imatge_fondo_login.jpg'
import './Login.css'

export default function Login() {
  const [isSignUp, setIsSignUp] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [passwordStrength, setPasswordStrength] = useState('')
  const [passwordMatch, setPasswordMatch] = useState(true)
  const navigate = useNavigate()
  const { signIn, signUp, user } = useAuth()

  // Debug: Log quan canvia l'estat de user
  useEffect(() => {
    console.log('👤 Estat de user canviat:', user ? `✅ Autenticat: ${user.email}` : '❌ No autenticat')
  }, [user])

  // Redirigir automàticament quan l'usuari estigui autenticat
  useEffect(() => {
    if (user && loading) {
      console.log('✅ Usuari autenticat detectat, redirigint...')
      setLoading(false)
      // Petit retard per assegurar que l'estat s'actualitzi
      setTimeout(() => {
        navigate('/', { replace: true })
      }, 100)
    }
  }, [user, loading, navigate])

  const handleChange = e => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Validar fortalesa de contrasenya en temps real
    if (name === 'password' && isSignUp) {
      validatePasswordStrength(value)
    }

    // Verificar que les contrasenyes coincideixin
    if (name === 'confirmPassword' && isSignUp) {
      setPasswordMatch(value === formData.password)
    }
    if (name === 'password' && isSignUp && formData.confirmPassword) {
      setPasswordMatch(value === formData.confirmPassword)
    }
  }

  // Funció per validar la fortalesa de la contrasenya
  const validatePasswordStrength = password => {
    if (password.length === 0) {
      setPasswordStrength('')
      return
    }

    let strength = 0
    let feedback = []

    // Longitud
    if (password.length >= 8) strength++
    else feedback.push('mínim 8 caràcters')

    // Majúscules
    if (/[A-Z]/.test(password)) strength++
    else feedback.push('una majúscula')

    // Minúscules
    if (/[a-z]/.test(password)) strength++
    else feedback.push('una minúscula')

    // Números
    if (/[0-9]/.test(password)) strength++
    else feedback.push('un número')

    // Caràcters especials
    if (/[^A-Za-z0-9]/.test(password)) strength++
    else feedback.push('un caràcter especial (!@#$%)')

    if (strength <= 2) {
      setPasswordStrength('dèbil')
    } else if (strength <= 3) {
      setPasswordStrength('mitjana')
    } else if (strength <= 4) {
      setPasswordStrength('forta')
    } else {
      setPasswordStrength('molt forta')
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validacions addicionals per al registre
    if (isSignUp) {
      // Verificar que les contrasenyes coincideixin
      if (formData.password !== formData.confirmPassword) {
        setError('Les contrasenyes no coincideixen')
        setLoading(false)
        return
      }

      // Verificar longitud mínima
      if (formData.password.length < 8) {
        setError('La contrasenya ha de tenir almenys 8 caràcters')
        setLoading(false)
        return
      }

      // Verificar complexitat mínima
      const hasUpperCase = /[A-Z]/.test(formData.password)
      const hasLowerCase = /[a-z]/.test(formData.password)
      const hasNumbers = /\d/.test(formData.password)

      if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
        setError('La contrasenya ha de contenir majúscules, minúscules i números')
        setLoading(false)
        return
      }

      // Verificar que el nom no estigui buit
      if (!formData.name.trim()) {
        setError('El nom és obligatori')
        setLoading(false)
        return
      }
    }

    console.log('🔐 Iniciant sessió amb:', formData.email)

    try {
      if (isSignUp) {
        // Registre
        console.log('📝 Registrant nou usuari...')
        const { error: signUpError } = await signUp(formData.email, formData.password, {
          name: formData.name,
        })

        if (signUpError) {
          console.error('❌ Error en registre:', signUpError)
          setError(signUpError.message)
          setLoading(false)
        } else {
          console.log('✅ Usuari registrat correctament')
          console.log('⏳ Esperant autenticació completa...')
          // El loading es manté i la redirecció s'activarà automàticament quan user s'actualitzi
        }
      } else {
        // Login
        console.log('🔑 Iniciant sessió...')
        const { error: signInError } = await signIn(formData.email, formData.password)

        if (signInError) {
          console.error('❌ Error en login:', signInError)
          setError(signInError.message)
          setLoading(false)
        } else {
          console.log('✅ Credencials verificades correctament')
          console.log('⏳ Esperant autenticació completa...')
          // El loading es manté i la redirecció s'activarà automàticament quan user s'actualitzi
        }
      }
    } catch (err) {
      console.error('❌ Error inesperat:', err)
      setError(err.message)
      setLoading(false)
    }
  }

  const togglePanel = () => {
    setIsSignUp(!isSignUp)
    setFormData({ name: '', email: '', password: '', confirmPassword: '' })
    setError('')
    setPasswordStrength('')
    setPasswordMatch(true)
    setLoading(false)
  }

  return (
    <div className='login-page' style={{ backgroundImage: `url(${fondoLogin})` }}>
      <div className={`container ${isSignUp ? 'right-panel-active' : ''}`}>
        {/* Formulari d'Iniciar Sessió */}
        <div className='form-container sign-in-container'>
          <form onSubmit={handleSubmit}>
            <h1>Iniciar Sesión</h1>
            <input type='email' name='email' placeholder='Email' value={formData.email} onChange={handleChange} required />
            <input type='password' name='password' placeholder='Contraseña' value={formData.password} onChange={handleChange} required />
            <a href='#'>Olvido Su Contraseña?</a>
            {error && <p style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>{error}</p>}
            <button type='submit' disabled={loading}>
              {loading ? 'Cargando...' : 'Iniciar Sesión'}
            </button>
          </form>
        </div>

        {/* Formulari de Registre */}
        <div className='form-container sign-up-container'>
          <form onSubmit={handleSubmit}>
            <h1>Crear Cuenta</h1>

            <input type='text' name='name' placeholder='Nombre *' value={formData.name} onChange={handleChange} required />

            <input type='email' name='email' placeholder='Email *' value={formData.email} onChange={handleChange} required />

            <input type='password' name='password' placeholder='Contraseña *' value={formData.password} onChange={handleChange} required minLength='8' />

            {/* Indicador de fortaleza de contraseña */}
            {formData.password && (
              <div
                style={{
                  fontSize: '12px',
                  marginTop: '-5px',
                  marginBottom: '5px',
                  color: passwordStrength === 'débil' ? '#ff6b6b' : passwordStrength === 'media' ? '#ffd93d' : passwordStrength === 'fuerte' ? '#6bcf7f' : '#51cf66',
                }}
              >
                Contraseña {passwordStrength}
              </div>
            )}

            <input type='password' name='confirmPassword' placeholder='Confirmar Contraseña *' value={formData.confirmPassword} onChange={handleChange} required minLength='8' />

            {/* Mensaje si las contraseñas no coinciden */}
            {formData.confirmPassword && !passwordMatch && <p style={{ color: '#ff6b6b', fontSize: '12px', marginTop: '-5px', marginBottom: '5px' }}>⚠️ Las contraseñas no coinciden</p>}

            {/* Mensaje de éxito si coinciden */}
            {formData.confirmPassword && passwordMatch && formData.password.length >= 8 && (
              <p style={{ color: '#51cf66', fontSize: '12px', marginTop: '-5px', marginBottom: '5px' }}>✓ Las contraseñas coinciden</p>
            )}

            {/* Requisitos de contraseña */}
            {formData.password && (
              <div style={{ fontSize: '11px', color: '#888', margin: '8px 0', textAlign: 'left', paddingLeft: '10px' }}>
                La contraseña debe tener:
                <ul style={{ margin: '3px 0', paddingLeft: '20px' }}>
                  <li style={{ color: formData.password.length >= 8 ? '#51cf66' : '#ff6b6b', margin: '2px 0' }}>Mínimo 8 caracteres</li>
                  <li style={{ color: /[A-Z]/.test(formData.password) ? '#51cf66' : '#ff6b6b', margin: '2px 0' }}>Una letra mayúscula</li>
                  <li style={{ color: /[a-z]/.test(formData.password) ? '#51cf66' : '#ff6b6b', margin: '2px 0' }}>Una letra minúscula</li>
                  <li style={{ color: /[0-9]/.test(formData.password) ? '#51cf66' : '#ff6b6b', margin: '2px 0' }}>Un número</li>
                </ul>
              </div>
            )}

            {error && <p style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>{error}</p>}

            <button type='submit' disabled={loading || (formData.confirmPassword && !passwordMatch)}>
              {loading ? 'Registrando...' : 'Registrarse'}
            </button>
          </form>
        </div>

        {/* Panells de benvinguda */}
        <div className='overlay-container'>
          <div className='overlay'>
            <div className='overlay-panel overlay-left'>
              <h1>¡Welcome Welcome!</h1>
              <p>Inicia sesión para acceder a tu cuenta y continuar donde lo dejaste</p>
              <button className='ghost' onClick={togglePanel}>
                INICIAR SESIÓN
              </button>
            </div>
            <div className='overlay-panel overlay-right'>
              <h1>¡Bienvenido de nuevo!</h1>
              <p>Regístrate con tus datos personales para comenzar tu viaje con nosotros</p>
              <button className='ghost' onClick={togglePanel}>
                CREAR CUENTA
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
